/* eslint-disable no-debugger */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import {
  getClipBroadcastId,
  getClipFPS,
  isLayoutWithVisionDataEnabledPure
} from '@goldcast/player-config/src/utils/clipUtils';
import { roundToNDecimalPlaces } from '@goldcast/player-config/src/utils/mathUtils';
import { LayoutChange } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/LayoutChange';
import { VideoCropPosition } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { ScreenshareBound } from '@goldcast/player-config/src/types/speakers-analysis';
import { getCropPosition } from '@goldcast/player-config/src/utils/cropUtils';
import { SCREENSHARE_VIDEO_ID } from '@goldcast/player-config/src/constants/screenshare';
import { getFinalConfigForLayout } from '@goldcast/player-config/src/utils/layout';
import { getAspectRatio } from '@goldcast/player-config/src/utils/sizeUtils';
import { CSSProperties } from '@goldcast/player-config/src/types/css-properties';
import { selectSpeakerAnalysis } from '@goldcast/player-config/src/selectors/contentMetadataStoreSelectors';
import { CropSelectionOutline } from './CropSelectionOutline';
import { getDebugVideoCropBoundsForUploads } from './videoCropBoundsUtils';
import ContentDialog from '@/Pages/Sessions/uiComponents/ContentDialog/ContentDialog';
import Button from '@/components/atoms/Button/Button';
import { updateClipData } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/constants';
import { currentClip } from '@/stores/clip';
import { remotionPlayerStateStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/remotionPlayerState';
import { RemotionPlayerContext } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/context/RemotionPlayerContext';
import { formatTime } from '@/libs/core';
import Icon from '@/components/atoms/Icon';
import { useTimelineSpeakers } from '@/hooks/useTimelineSpeakers';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import { getCompositeSegment } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/playerConfigUtil';
import useAnalytics from '@/hooks/useAnalytics';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import Toggle from '@/components/atoms/Toggle';
import {
  getSegmentAtCurrentFrame,
  getSegmentTimeFromPlayerTime
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/timeUtils';

interface DebugVideoCropContainerProps {
  videoUrl: string;
  clipId: string;
  selectedVideoId: string;
  compositeSegmentId: string;
  isOpen: boolean;
  onClose: () => void;
  onCropComplete?: (cropData: CropData) => void;
  containerAspectRatio: number;
  videoAspectRatio: number;
  maintainAspectRatio?: boolean;
}

interface CropData {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DebugCropBounds {
  facialBound?: Record<string, VideoCropPosition>;
  personBound?: Record<string, VideoCropPosition>;
  personContainerBound?: Record<string, VideoCropPosition>;
  videoCropPositionMap: Record<string, VideoCropPosition>;
  faceBoundingBoxMap: Record<string, VideoCropPosition>;
  personContainerBoundingBoxMap: Record<string, VideoCropPosition>;
  availableSpeakerIds: string[];
}

type TimePanelEntryKind = 'face' | 'screenshare-start' | 'screenshare-end';

interface TimePanelEntry {
  time: number;
  kind: TimePanelEntryKind;
}

// Resolve the screenshare range that applies at `time`. Prefers the range that strictly
// contains the time; at an off-boundary or inside a gap it falls back to the most recent range
// that has already started so the preview still shows the relevant screenshare crop.
const getActiveScreenshareBound = (bounds: ScreenshareBound[], time: number): ScreenshareBound | undefined => {
  const containingBound = bounds.find(bound => bound.start_time <= time && time < bound.end_time);
  if (containingBound) {
    return containingBound;
  }
  const startedBounds = bounds.filter(bound => bound.start_time <= time);
  if (startedBounds.length > 0) {
    return startedBounds.reduce((latest, bound) => (bound.start_time > latest.start_time ? bound : latest));
  }
  return bounds[0];
};

const getLayoutChangeCropPositions = (
  layoutChange: LayoutChange,
  selectedVideoId: string,
  parentContainerAspectRatio: number,
  videoAspectRatio: number
) => {
  const oldCropPositions = layoutChange.videoCropPositionMap?.[selectedVideoId];
  if (!oldCropPositions) {
    const shouldFitByWidth = parentContainerAspectRatio > videoAspectRatio;
    if (shouldFitByWidth) {
      return {
        left: 0,
        top: 0.5 - (0.5 * videoAspectRatio) / parentContainerAspectRatio,
        width: 1,
        height: (1 * videoAspectRatio) / parentContainerAspectRatio
      };
    }
    return {
      top: 0,
      left: 0.5 - (0.5 * parentContainerAspectRatio) / videoAspectRatio,
      width: (1 * parentContainerAspectRatio) / videoAspectRatio,
      height: 1
    };
  }
  return {
    left: oldCropPositions.top_left.x || 0,
    top: oldCropPositions.top_left.y || 0,
    width: oldCropPositions.bottom_right.x - oldCropPositions.top_left.x || 1,
    height: oldCropPositions.bottom_right.y - oldCropPositions.top_left.y || 1
  };
};

export default function DebugVideoCropContainer({
  videoUrl,
  clipId,
  selectedVideoId,
  compositeSegmentId,
  isOpen,
  onClose,
  maintainAspectRatio = true,
  containerAspectRatio,
  videoAspectRatio
}: DebugVideoCropContainerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { playerRef } = useContext(RemotionPlayerContext);

  const [videoLoading, setVideoLoading] = useState(true);

  // Get speakers analysis data
  const clip = currentClip.getSnapshot()[clipId];
  const fps = clip ? getClipFPS(clip) : 30;
  const compositeSegment = clip && getCompositeSegment(clip, compositeSegmentId);
  const trimOffset = roundToNDecimalPlaces(compositeSegment ? compositeSegment.currentBounds.fromFrame / fps : 0, 4);
  const offset = roundToNDecimalPlaces(compositeSegment ? compositeSegment.fromFrame / fps : 0, 4);
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<string>(selectedVideoId);
  const { speakers, screenShareSpeakerDetails, screenshareExists } = useTimelineSpeakers();
  const { seekTo } = useRemotionPlayerSeek();

  const isScreenshareSelected = selectedSpeakerId === SCREENSHARE_VIDEO_ID;

  const [currentCompositeFrame, setCurrentCompositeFrame] = useState(
    roundToNDecimalPlaces((playerRef?.getCurrentFrame() || 0) + trimOffset * fps - offset * fps, 4)
  );

  const speakersAnalysis = useContentMetadataStore(selectSpeakerAnalysis(getClipBroadcastId(clip) ?? ''));

  // Get speaker details and video-to-speaker mapping

  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const [showBoundForSelectedSpeakerOnly, setShowBoundForSelectedSpeakerOnly] = useState<boolean>(false);
  const [usePersonContainerForPreview, setUsePersonContainerForPreview] = useState(true);
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isPersonContainerFeatureEnabled = Boolean(
    featureFlags[FeatureFlagKeys.Use_CL_Person_Container] &&
      clip &&
      isLayoutWithVisionDataEnabledPure({
        clip,
        isFullFaceRecEnabled: Boolean(featureFlags[FeatureFlagKeys.Use_CL_Fullvideo_Facerec]),
        isFullFaceRecForPreRecordedEnabled: Boolean(featureFlags[FeatureFlagKeys.Use_CL_Fullvideo_Facerec_Pre_Recorded])
      })
  );
  // Get the speaker key for the selected video

  const currentLayoutChange = useMemo(() => {
    if (!compositeSegment) {
      return null;
    }
    return compositeSegment.layoutChanges.find(
      layoutChange =>
        roundToNDecimalPlaces(layoutChange.fromFrame, 4) <=
          roundToNDecimalPlaces(currentCompositeFrame - trimOffset * fps, 4) &&
        roundToNDecimalPlaces(layoutChange.toFrame, 4) >
          roundToNDecimalPlaces(currentCompositeFrame - trimOffset * fps, 4)
    );
  }, [compositeSegment, currentCompositeFrame, trimOffset, fps]);

  const speakersInTheLayout = useMemo(() => {
    return speakers.filter(speaker => currentLayoutChange?.videoIds.includes(speaker.key));
  }, [currentLayoutChange?.videoIds, speakers]);

  // Expose the shared screen alongside the speakers so it can be selected in the panel.
  // It is keyed by the screenshare video id (not the speaker key) so selection flows
  // through the same crop/save logic as a regular video.
  const speakersToShow = useMemo(() => {
    if (screenshareExists && screenShareSpeakerDetails) {
      return [...speakers, { ...screenShareSpeakerDetails, key: SCREENSHARE_VIDEO_ID }];
    }
    return speakers;
  }, [speakers, screenshareExists, screenShareSpeakerDetails]);

  // Single source of truth for the time currently being inspected, in segment seconds. It
  // defaults to the live player position and switches to whatever the user picks in the Time
  // Analysis panel (a face-recognition point or a screenshare on/off boundary).
  const activeSegmentTime = useMemo(
    () => (selectedTime !== null ? selectedTime : roundToNDecimalPlaces(currentCompositeFrame / fps, 4)),
    [selectedTime, currentCompositeFrame, fps]
  );

  // Screen share crops are not derived from face detection. Instead the crop region for the
  // currently viewed time comes straight from the analysis' screenshare bounds. The viewed time
  // often lands on a face-recognition point that sits inside a screenshare range rather than on
  // its exact start, so we resolve the range that contains it.
  const screenshareBound = useMemo<VideoCropPosition | undefined>(() => {
    const screenshareBounds = speakersAnalysis?.screenshare_bounds;
    if (!isScreenshareSelected || !screenshareBounds?.length) {
      return undefined;
    }
    const activeBound = getActiveScreenshareBound(screenshareBounds, activeSegmentTime);
    if (!activeBound) {
      return undefined;
    }
    const { x, y, width, height } = activeBound.coordinates;
    return {
      top_left: { x, y },
      bottom_right: { x: x + width, y: y + height }
    };
  }, [isScreenshareSelected, speakersAnalysis, activeSegmentTime]);

  // The Time Analysis panel lists face-recognition sample points. When the clip also has a
  // shared screen, we merge in the times where it turns on and off so the user can jump straight
  // to those transitions. Entries are de-duplicated by time; a screenshare boundary that
  // coincides with a face point keeps the screenshare label.
  const timePanelEntries = useMemo<TimePanelEntry[]>(() => {
    const entriesByTime = new Map<number, TimePanelEntry>();

    const upsertEntry = (time: number, kind: TimePanelEntryKind) => {
      const key = roundToNDecimalPlaces(time, 4);
      const existing = entriesByTime.get(key);
      if (!existing || (existing.kind === 'face' && kind !== 'face')) {
        entriesByTime.set(key, { time: key, kind });
      }
    };

    speakersAnalysis?.times?.forEach(time => upsertEntry(time, 'face'));
    if (screenshareExists) {
      speakersAnalysis?.screenshare_bounds?.forEach(bound => {
        upsertEntry(bound.start_time, 'screenshare-start');
        upsertEntry(bound.end_time, 'screenshare-end');
      });
    }

    return [...entriesByTime.values()].sort((a, b) => a.time - b.time);
  }, [speakersAnalysis, screenshareExists]);

  const [cropPositions, setCropPositions] = useState(
    currentLayoutChange && selectedSpeakerId
      ? getLayoutChangeCropPositions(currentLayoutChange, selectedSpeakerId, containerAspectRatio, videoAspectRatio)
      : {
          left: 0,
          top: 0,
          width: 1,
          height: 1
        }
  );

  useEffect(() => {
    if (currentLayoutChange && selectedSpeakerId) {
      setCropPositions(
        getLayoutChangeCropPositions(currentLayoutChange, selectedSpeakerId, containerAspectRatio, videoAspectRatio)
      );
    }
  }, [containerAspectRatio, currentLayoutChange, selectedSpeakerId, videoAspectRatio]);

  useEffect(() => {
    if (videoRef.current && !videoLoading && isOpen) {
      videoRef.current.currentTime = currentCompositeFrame / fps;
    }
  }, [currentCompositeFrame, fps, videoLoading, isOpen]);

  // Calculate the face-recognition sample nearest the current player position; this seeds the
  // Time Analysis selection when the dialog opens.
  const initialSelectedTime = useMemo(() => {
    if (!speakersAnalysis?.times?.length || !compositeSegment) {
      return null;
    }
    const currentTime = remotionPlayerStateStore.getSnapshot().currentTime;
    const currentSegment = getSegmentAtCurrentFrame(currentTime, clip);
    if (!currentSegment) {
      return speakersAnalysis.times[0] ?? null;
    }

    const segmentTime = getSegmentTimeFromPlayerTime(currentTime, clip, currentSegment.id);
    const index = Math.max(
      speakersAnalysis.times.findLastIndex(t => t <= segmentTime),
      0
    );

    return speakersAnalysis.times[index] ?? null;
  }, [clip, compositeSegment, speakersAnalysis]);

  // Initialize selected time when dialog opens
  useEffect(() => {
    if (isOpen && selectedTime === null && initialSelectedTime !== null) {
      setSelectedTime(initialSelectedTime);
    }
    // Reset when dialog closes
    if (!isOpen) {
      setSelectedTime(null);
      setSelectedSpeakerId('');
      setShowBoundForSelectedSpeakerOnly(false);
      setUsePersonContainerForPreview(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialSelectedTime]);

  // Handle time analysis selection. `time` is in segment seconds and may be a face-recognition
  // point or a screenshare on/off boundary; both flow through the same time-based resolution.
  const handleTimeAnalysisSelect = useCallback(
    (time: number) => {
      if (!compositeSegment) {
        return;
      }
      setSelectedTime(time);
      setCurrentCompositeFrame(roundToNDecimalPlaces(Math.max(0, time * fps), 4));
    },
    [compositeSegment, fps]
  );

  const setIsOpen = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onClose();
      }
    },
    [onClose]
  );

  const updateCropPositions = useCallback(newPositions => {
    setCropPositions(newPositions);
  }, []);
  const { trackManualRepositioningApplied } = useAnalytics();

  const handleCropComplete = useCallback(() => {
    const newCropPositions = {
      top_left: {
        x: cropPositions.left,
        y: cropPositions.top
      },
      bottom_right: {
        x: cropPositions.left + cropPositions.width,
        y: cropPositions.top + cropPositions.height
      }
    };
    if (!compositeSegment) {
      return;
    }
    const currentScene = compositeSegment.scenes.find(
      scene => scene.fromFrame <= currentCompositeFrame && scene.toFrame > currentCompositeFrame
    );
    if (!currentScene) {
      return;
    }
    updateClipData({
      clipId,
      actionType: CLIP_UPDATE_TYPE.UPDATE_SCENE_CROP_POSITION,
      compositeId: compositeSegmentId,
      cropPosition: newCropPositions,
      sceneId: currentScene.id,
      videoId: selectedSpeakerId
    });
    trackManualRepositioningApplied({ clipId, fromRemotionPlayer: true });
    seekTo(currentCompositeFrame / fps - trimOffset + offset);
    onClose();
  }, [
    clipId,
    compositeSegment,
    compositeSegmentId,
    cropPositions.height,
    cropPositions.left,
    cropPositions.top,
    cropPositions.width,
    currentCompositeFrame,
    fps,
    offset,
    onClose,
    seekTo,
    selectedSpeakerId,
    trackManualRepositioningApplied,
    trimOffset
  ]);

  const {
    facialBound,
    personBound,
    personContainerBound,
    videoCropPositionMap,
    faceBoundingBoxMap,
    personContainerBoundingBoxMap,
    availableSpeakerIds
  } = useMemo<DebugCropBounds>(() => {
    // The shared screen is selectable whenever the clip has one; this is independent of the
    // current selection so the user can always switch to it (or back to a face speaker).
    const screenshareAvailableIds = screenshareExists && screenShareSpeakerDetails ? [SCREENSHARE_VIDEO_ID] : [];

    // Resolve the face/person analysis for the currently inspected time. This is computed
    // regardless of the selection because it both feeds the face display and decides which
    // speakers are selectable; resolving it off the shared time makes a screenshare boundary
    // selection surface the faces that fall within that range.
    let faceBounds: ReturnType<typeof getDebugVideoCropBoundsForUploads>;
    if (clip && compositeSegment && currentLayoutChange && speakersAnalysis?.time_analysis?.length) {
      const timeIndex = Math.max(
        0,
        Math.min(
          Math.max(
            speakersAnalysis.times.findLastIndex(t => t <= activeSegmentTime),
            0
          ),
          speakersAnalysis.time_analysis.length - 1
        )
      );
      faceBounds = getDebugVideoCropBoundsForUploads({
        facePositions: structuredClone(speakersAnalysis.time_analysis[timeIndex].face_positions),
        speakerIdToFaceMap: speakersAnalysis.speaker_mapping,
        broadcastId: getClipBroadcastId(clip)!
      });
    }

    const videoCropPositionMap = faceBounds?.videoCropPositionMap ?? {};
    const faceBoundingBoxMap = faceBounds?.faceBoundingBoxMap ?? {};
    const personBoundingBoxMap = faceBounds?.personBoundingBoxMap ?? {};
    const personContainerBoundingBoxMap = faceBounds?.personContainerBoundingBoxMap ?? {};

    // Only speakers in the layout who have a person bound at this time are selectable.
    const faceAvailableIds =
      speakersInTheLayout?.filter(speaker => personBoundingBoxMap[speaker.key]).map(speaker => speaker.key) ?? [];
    const availableSpeakerIds = [...faceAvailableIds, ...screenshareAvailableIds];

    // Screen share is displayed from its own detected bound rather than face/person analysis.
    if (isScreenshareSelected) {
      const screenshareMap: Record<string, VideoCropPosition> = screenshareBound
        ? { [SCREENSHARE_VIDEO_ID]: screenshareBound }
        : {};
      return {
        facialBound: {},
        personBound: screenshareMap,
        personContainerBound: {},
        videoCropPositionMap: screenshareMap,
        faceBoundingBoxMap: {},
        personContainerBoundingBoxMap: {},
        availableSpeakerIds
      };
    }

    // Filter person bounds to show only selected speaker if checkbox is checked
    let filteredPersonBoundingBoxMap = personBoundingBoxMap;
    let filteredFaceBoundingBoxMap = faceBoundingBoxMap;
    let filteredPersonContainerBoundingBoxMap = personContainerBoundingBoxMap;
    if (showBoundForSelectedSpeakerOnly && selectedSpeakerId) {
      // selectedSpeakerId is already a videoId, so use it directly
      if (personBoundingBoxMap[selectedSpeakerId]) {
        filteredPersonBoundingBoxMap = {
          [selectedSpeakerId]: personBoundingBoxMap[selectedSpeakerId]
        };
        filteredFaceBoundingBoxMap = faceBoundingBoxMap[selectedSpeakerId]
          ? {
              [selectedSpeakerId]: faceBoundingBoxMap[selectedSpeakerId]
            }
          : filteredFaceBoundingBoxMap;
        filteredPersonContainerBoundingBoxMap = personContainerBoundingBoxMap[selectedSpeakerId]
          ? {
              [selectedSpeakerId]: personContainerBoundingBoxMap[selectedSpeakerId]
            }
          : {};
      } else {
        filteredPersonBoundingBoxMap = {};
        filteredFaceBoundingBoxMap = {};
        filteredPersonContainerBoundingBoxMap = {};
      }
    }

    return {
      facialBound: filteredFaceBoundingBoxMap,
      personBound: filteredPersonBoundingBoxMap,
      personContainerBound: filteredPersonContainerBoundingBoxMap,
      videoCropPositionMap,
      faceBoundingBoxMap,
      personContainerBoundingBoxMap,
      availableSpeakerIds
    };
  }, [
    clip,
    compositeSegment,
    currentLayoutChange,
    activeSegmentTime,
    speakersInTheLayout,
    showBoundForSelectedSpeakerOnly,
    selectedSpeakerId,
    speakersAnalysis,
    isScreenshareSelected,
    screenshareBound,
    screenshareExists,
    screenShareSpeakerDetails
  ]);

  const calculatedCropPosition = useMemo(() => {
    if (
      isScreenshareSelected ||
      !isPersonContainerFeatureEnabled ||
      !currentLayoutChange ||
      !selectedSpeakerId ||
      !clip
    ) {
      return undefined;
    }
    const videoIndex = currentLayoutChange.videoIds.indexOf(selectedSpeakerId);
    const finalConfig = getFinalConfigForLayout(currentLayoutChange, currentLayoutChange.videoIds);
    const selectedCropPosition = videoCropPositionMap[selectedSpeakerId];
    const selectedConfig = finalConfig[videoIndex];
    if (videoIndex < 0 || !selectedCropPosition || !selectedConfig?.containerStyle) {
      return undefined;
    }

    const allOtherCropInformation = {
      ...Object.fromEntries(Object.entries(faceBoundingBoxMap).filter(([videoId]) => videoId !== selectedSpeakerId)),
      ...Object.fromEntries(
        Object.entries(videoCropPositionMap).filter(
          ([videoId]) => videoId !== selectedSpeakerId && currentLayoutChange.videoIds.includes(videoId)
        )
      )
    };

    return getCropPosition(
      selectedCropPosition,
      selectedConfig.containerStyle as CSSProperties,
      getAspectRatio(clip.asset_metadata.size),
      allOtherCropInformation,
      false,
      videoAspectRatio,
      faceBoundingBoxMap[selectedSpeakerId],
      usePersonContainerForPreview ? personContainerBoundingBoxMap[selectedSpeakerId] : undefined
    );
  }, [
    clip,
    currentLayoutChange,
    faceBoundingBoxMap,
    isPersonContainerFeatureEnabled,
    isScreenshareSelected,
    personContainerBoundingBoxMap,
    selectedSpeakerId,
    usePersonContainerForPreview,
    videoAspectRatio,
    videoCropPositionMap
  ]);

  useEffect(() => {
    if (!calculatedCropPosition) {
      return;
    }
    setCropPositions({
      left: calculatedCropPosition.top_left.x,
      top: calculatedCropPosition.top_left.y,
      width: calculatedCropPosition.bottom_right.x - calculatedCropPosition.top_left.x,
      height: calculatedCropPosition.bottom_right.y - calculatedCropPosition.top_left.y
    });
  }, [calculatedCropPosition]);

  useEffect(() => {
    if (isOpen) {
      setSelectedSpeakerId(selectedVideoId);
    }
  }, [isOpen, selectedVideoId]);

  // Validate and update selected speaker when time analysis changes
  useEffect(() => {
    if (!isOpen || availableSpeakerIds.length === 0) {
      return;
    }

    // If selected speaker is not available in current time analysis, switch to first available
    if (selectedSpeakerId && !availableSpeakerIds.includes(selectedSpeakerId)) {
      const firstAvailableSpeakerId = availableSpeakerIds[0];
      if (firstAvailableSpeakerId) {
        setSelectedSpeakerId(firstAvailableSpeakerId);
      }
    } else if (!selectedSpeakerId && availableSpeakerIds.length > 0) {
      // If no speaker is selected, select the first available one
      setSelectedSpeakerId(availableSpeakerIds[0]);
    }
  }, [isOpen, selectedSpeakerId, availableSpeakerIds]);

  return (
    <div onPointerDown={e => e.stopPropagation()}>
      <ContentDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        title={'Crop Video'}
        size="large"
        panelClassNames="!max-w-6xl"
      >
        <div className="flex flex-col p-6">
          <div className="mb-6 flex gap-4">
            {/* Speakers Panel */}
            <div className="flex w-48 flex-col border-r border-slate-200 pr-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">Speakers</h3>
              <div className="mb-3 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="showPersonBoundForSelectedOnly"
                  checked={showBoundForSelectedSpeakerOnly}
                  onChange={e => setShowBoundForSelectedSpeakerOnly(e.target.checked)}
                  className="size-4 rounded border-gray-300 text-deep-orange focus:ring-2 focus:ring-theme-input-focus focus:ring-offset-2"
                />
                <label htmlFor="showPersonBoundForSelectedOnly" className="text-xs text-slate-600">
                  Show bound for selected only
                </label>
              </div>
              <div className="flex max-h-[calc(75vh-200px)] flex-col gap-2 overflow-y-auto">
                {speakersToShow.length > 0 ? (
                  speakersToShow.map((speakerInfo, index) => {
                    const isSelected = selectedSpeakerId === speakerInfo.key;
                    const isAvailable = speakerInfo.key ? availableSpeakerIds.includes(speakerInfo.key) : false;

                    return (
                      <button
                        key={speakerInfo.key}
                        onClick={() => {
                          if (isAvailable) {
                            setSelectedSpeakerId(speakerInfo.key || '');
                          }
                        }}
                        disabled={!isAvailable}
                        className={`rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : !isAvailable
                              ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'
                              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                        title={
                          isSelected
                            ? 'Current video speaker'
                            : !isAvailable
                              ? 'Speaker not available in this time analysis'
                              : ''
                        }
                      >
                        {speakerInfo?.first_name || speakerInfo?.last_name
                          ? `${speakerInfo.first_name || ''} ${speakerInfo.last_name || ''}`.trim()
                          : speakerInfo?.key || `Speaker ${index + 1}`}
                        {isSelected && <span className="ml-1 text-xs">(Current)</span>}
                        {!isAvailable && !isSelected && <span className="ml-1 text-xs">(Unavailable)</span>}
                      </button>
                    );
                  })
                ) : (
                  <div className="py-4 text-center text-sm text-slate-500">No speakers available</div>
                )}
              </div>
            </div>

            {/* Time Analysis Panel */}
            <div className="flex w-48 flex-col border-r border-slate-200 pr-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">Time Analysis</h3>
              <div className="flex max-h-[calc(75vh-200px)] flex-col gap-2 overflow-y-auto">
                {timePanelEntries.length > 0 ? (
                  timePanelEntries.map(entry => {
                    const isSelected =
                      selectedTime !== null &&
                      roundToNDecimalPlaces(entry.time, 4) === roundToNDecimalPlaces(selectedTime, 4);
                    return (
                      <button
                        key={`${entry.time}-${entry.kind}`}
                        onClick={() => handleTimeAnalysisSelect(entry.time)}
                        className={`rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {formatTime(entry.time)}
                        {entry.kind === 'screenshare-start' && (
                          <span className="ml-1 text-xs text-slate-500">(screen on)</span>
                        )}
                        {entry.kind === 'screenshare-end' && (
                          <span className="ml-1 text-xs text-slate-500">(screen off)</span>
                        )}
                      </button>
                    );
                  })
                ) : (
                  <div className="py-4 text-center text-sm text-slate-500">No time analysis available</div>
                )}
              </div>
            </div>

            {/* Cropping UI Panel */}
            <div className="flex flex-1 flex-col">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">Cropping UI</h3>
              {isPersonContainerFeatureEnabled && !isScreenshareSelected && (
                <div
                  className="mb-3 rounded-md border border-slate-200 bg-slate-50 p-3"
                  data-testid="person-container-preview"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-xs font-medium text-slate-700">Use person-container boundary</div>
                      <div className="text-xs text-slate-500">
                        {personContainerBoundingBoxMap[selectedSpeakerId]
                          ? 'Toggle to compare the generated upload crop.'
                          : 'No container was detected for this person.'}
                      </div>
                    </div>
                    <Toggle
                      size="small"
                      on={Boolean(personContainerBoundingBoxMap[selectedSpeakerId]) && usePersonContainerForPreview}
                      onToggle={setUsePersonContainerForPreview}
                      disabled={!personContainerBoundingBoxMap[selectedSpeakerId]}
                      title="Use person-container boundary in crop preview"
                    />
                  </div>
                  {calculatedCropPosition && (
                    <div className="mt-2 font-mono text-xs text-slate-600" data-testid="calculated-crop-position">
                      Crop: ({calculatedCropPosition.top_left.x.toFixed(4)},{' '}
                      {calculatedCropPosition.top_left.y.toFixed(4)}) → ({' '}
                      {calculatedCropPosition.bottom_right.x.toFixed(4)},{' '}
                      {calculatedCropPosition.bottom_right.y.toFixed(4)})
                    </div>
                  )}
                </div>
              )}
              <div className="relative flex flex-1 items-center justify-center overflow-hidden">
                <div ref={containerRef} className="relative overflow-hidden">
                  {videoLoading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Icon name="IconLoader2" className="animate-spin" color="gray" size={20} stroke={2.5} />
                    </div>
                  )}
                  <video
                    src={videoUrl}
                    ref={videoRef}
                    onLoadedData={event => {
                      event.currentTarget.currentTime = currentCompositeFrame / fps;
                      setVideoLoading(false);
                    }}
                    className="size-full max-h-[calc(75vh-172px)] w-auto max-w-full object-contain"
                    style={{ opacity: videoLoading ? 0.5 : 1 }}
                    data-testid="crop-preview-image"
                  />

                  {/* Crop Selection Outline */}
                  <div className="absolute inset-0">
                    <CropSelectionOutline
                      facialBounds={facialBound ?? {}}
                      personBounds={personBound ?? {}}
                      personContainerBounds={isPersonContainerFeatureEnabled ? (personContainerBound ?? {}) : {}}
                      positions={cropPositions}
                      aspectRatio={containerAspectRatio}
                      videoAspectRatio={videoAspectRatio}
                      updatePositions={updateCropPositions}
                      maintainAspectRatio={maintainAspectRatio}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end border-t border-slate-200 pt-4">
            <div className="flex items-center space-x-3">
              <Button variation="outline" onClick={onClose} trackingId="video-crop-cancel-button">
                Cancel
              </Button>
              <Button variation="filled" onClick={handleCropComplete} trackingId="video-crop-save-button">
                Apply Crop
              </Button>
            </div>
          </div>
        </div>
      </ContentDialog>
    </div>
  );
}
